import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { db } from '../firebase';
import { collection, query, where, getDocs, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { format, parseISO, isPast, isFuture, addDays, isToday, formatDistanceToNow } from 'date-fns';
import {
  Leaf, Calendar, CreditCard, Clock, Phone, Mail, MapPin,
  ChevronRight, CheckCircle, AlertCircle, Camera, Plus,
  CalendarPlus, Shirt, FileText, LogOut, User, Home,
  DollarSign, Star, MessageSquare, ArrowLeft, X, Check,
  ExternalLink, Send, Inbox, Smartphone, Lock
} from 'lucide-react';
import { formatCurrency, formatDate } from '../utils/helpers';
import { sendLoginCode } from '../utils/notificationsApi';
import './ClientPortalMain.css';

// Helper: Mask email for display (show first 2 chars + domain)
function maskEmail(email) {
  if (!email) return '';
  const [local, domain] = email.split('@');
  if (local.length <= 2) return `${local}@${domain}`;
  return `${local.substring(0, 2)}${'•'.repeat(Math.min(local.length - 2, 6))}@${domain}`;
}

// Helper: Mask phone for display (show last 4 digits)
function maskPhone(phone) {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return phone;
  return `(•••) •••-${digits.slice(-4)}`;
}

// Helper: Mask street address for privacy (e.g. "123 Main St" → "1** Main St")
function maskAddress(address) {
  if (!address) return '';
  // Mask house/building number but keep street name for recognition
  return address.replace(/^\d+/, match => match[0] + '*'.repeat(match.length - 1));
}

// ===== PORTAL LOGIN =====
function PortalLogin({ onLogin, error: externalError }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loginInput, setLoginInput] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);

  // Forgot PIN flow states
  const [resetStep, setResetStep] = useState(null); // null | 'email' | 'verify' | 'newpin' | 'token'
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [generatedCode, setGeneratedCode] = useState(null);
  const [codeExpires, setCodeExpires] = useState(null);
  const [resetClientData, setResetClientData] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [tokenChecked, setTokenChecked] = useState(false);
  const [resetTokenValue, setResetTokenValue] = useState(null);

  // Check for remembered session on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('ts_portal_session');
      if (saved) {
        const session = JSON.parse(saved);
        if (session.clientData && session.expires > Date.now()) {
          console.log('Remembered session found, auto-logging in as:', session.clientData.firstName);
          onLogin(session.clientData);
          setCheckingSession(false);
          return;
        }
        localStorage.removeItem('ts_portal_session');
      }
    } catch (err) {
      console.error('Error checking remembered session:', err);
      localStorage.removeItem('ts_portal_session');
    }
    setCheckingSession(false);
  }, []);

  // Check for resetToken URL parameter (admin-sent PIN reset link)
  useEffect(() => {
    const resetToken = searchParams.get('resetToken');
    if (!resetToken || tokenChecked) return;
    setTokenChecked(true);

    (async () => {
      try {
        // Query Firestore for a client with this reset token
        const snap = await getDocs(
          query(collection(db, 'clients'), where('pinResetToken', '==', resetToken))
        );

        if (snap.empty) {
          setError('This reset link is invalid or has already been used.');
          // Clear the token from URL
          searchParams.delete('resetToken');
          setSearchParams(searchParams, { replace: true });
          return;
        }

        const clientDoc = snap.docs[0];
        const clientData = { id: clientDoc.id, ...clientDoc.data() };

        // Check if token has expired
        if (clientData.pinResetTokenExpires && new Date(clientData.pinResetTokenExpires) < new Date()) {
          setError('This reset link has expired. Please contact us to request a new one.');
          searchParams.delete('resetToken');
          setSearchParams(searchParams, { replace: true });
          return;
        }

        // Token is valid - go directly to set new PIN
        setResetClientData(clientData);
        setResetTokenValue(resetToken);
        setResetStep('token');

        // Clear the token from URL
        searchParams.delete('resetToken');
        setSearchParams(searchParams, { replace: true });
      } catch (err) {
        console.error('Error validating reset token:', err);
        setError('Something went wrong. Please try again or contact us.');
      }
    })();
  }, [searchParams, tokenChecked]);

  if (checkingSession) {
    return (
      <div className="portal-login">
        <div className="login-container">
          <div className="login-header">
            <div className="login-logo">
              <Leaf size={32} />
              <div>
                <h1>Two Sisters</h1>
                <span>Client Portal</span>
              </div>
            </div>
          </div>
          <div className="login-card" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
            <p style={{ color: '#64748b' }}>Checking your session...</p>
          </div>
        </div>
      </div>
    );
  }

  // Look up client by email (shared by login and reset flows)
  const findClientByEmail = async (email) => {
    const emailLower = email.toLowerCase();
    let snap = await getDocs(query(collection(db, 'clients'), where('email', '==', email)));
    if (snap.empty) {
      snap = await getDocs(query(collection(db, 'clients'), where('email', '==', emailLower)));
    }
    if (snap.empty) {
      const allSnap = await getDocs(collection(db, 'clients'));
      let found = null;
      allSnap.docs.forEach(d => {
        const c = { id: d.id, ...d.data() };
        if (c.email?.toLowerCase() === emailLower) found = c;
      });
      return found;
    }
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const input = loginInput.trim();
    const pwd = password.trim();

    if (!input || !pwd) {
      setError('Please enter your email and PIN.');
      setLoading(false);
      return;
    }

    if (pwd.length !== 4 || !/^\d{4}$/.test(pwd)) {
      setError('PIN must be exactly 4 digits.');
      setLoading(false);
      return;
    }

    try {
      let clientData = null;

      // Look up by email only
      clientData = await findClientByEmail(input);

      if (!clientData) {
        setError('Invalid email or PIN. Please try again.');
        setLoading(false);
        return;
      }

      // Verify PIN: check custom portalPin first, fall back to last 4 of phone
      const phoneDigits = clientData.phone?.replace(/\D/g, '') || '';
      const last4 = phoneDigits.slice(-4);
      const validPin = clientData.portalPin || last4;

      if (!validPin || pwd !== validPin) {
        setError('Invalid email or PIN. Please try again.');
        setLoading(false);
        return;
      }

      // Success! Save session
      try {
        const expires = Date.now() + (30 * 24 * 60 * 60 * 1000); // 30 days
        localStorage.setItem('ts_portal_session', JSON.stringify({
          clientData,
          expires
        }));
      } catch (err) {
        console.error('Error saving session:', err);
      }

      console.log('Login successful for:', clientData.firstName, clientData.lastName, 'ID:', clientData.id);
      onLogin(clientData);
    } catch (err) {
      console.error('Login error:', err);
      setError('Something went wrong. Please try again or contact us.');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Send verification code to email
  const handleSendResetCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const email = resetEmail.trim();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }

    try {
      const clientData = await findClientByEmail(email);
      if (!clientData) {
        // Don't reveal whether the email exists
        setError('If this email is on file, a verification code has been sent.');
        setLoading(false);
        return;
      }

      // Generate 6-digit code
      const code = String(Math.floor(100000 + Math.random() * 900000));
      const expires = Date.now() + (10 * 60 * 1000); // 10 minutes

      setGeneratedCode(code);
      setCodeExpires(expires);
      setResetClientData(clientData);

      // Send code via email
      await sendLoginCode({
        channel: 'email',
        email: clientData.email,
        code,
        clientName: clientData.firstName
      });

      setResetStep('verify');
    } catch (err) {
      console.error('Reset code error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify the code
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError(null);

    const code = resetCode.trim();
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setError('Please enter the 6-digit code from your email.');
      return;
    }

    if (Date.now() > codeExpires) {
      setError('Code has expired. Please request a new one.');
      return;
    }

    if (code !== generatedCode) {
      setError('Incorrect code. Please try again.');
      return;
    }

    setResetStep('newpin');
  };

  // Step 3: Save new PIN
  const handleSetNewPin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
      setError('PIN must be exactly 4 digits.');
      setLoading(false);
      return;
    }

    if (newPin !== confirmPin) {
      setError('PINs do not match.');
      setLoading(false);
      return;
    }

    try {
      // Save custom PIN via server-side API
      const res = await fetch('/.netlify/functions/portal-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'setPortalPin',
          clientId: resetClientData.id,
          newPin
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to set PIN');
      }

      // Reset flow state
      setResetStep(null);
      setResetEmail('');
      setResetCode('');
      setNewPin('');
      setConfirmPin('');
      setGeneratedCode(null);
      setCodeExpires(null);
      setResetClientData(null);
      setSuccessMessage('PIN updated! You can now sign in with your new PIN.');
    } catch (err) {
      console.error('Set PIN error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setResetStep(null);
    setResetEmail('');
    setResetCode('');
    setNewPin('');
    setConfirmPin('');
    setError(null);
    setGeneratedCode(null);
    setCodeExpires(null);
    setResetClientData(null);
  };

  // ===== FORGOT PIN: Enter email =====
  if (resetStep === 'email') {
    return (
      <div className="portal-login">
        <div className="login-container">
          <div className="login-header">
            <div className="login-logo">
              <Leaf size={32} />
              <div>
                <h1>Two Sisters</h1>
                <span>Client Portal</span>
              </div>
            </div>
          </div>
          <div className="login-card">
            <h2>Reset PIN</h2>
            <p className="login-subtitle">Enter your email and we'll send you a verification code</p>
            {error && (
              <div className="login-error"><AlertCircle size={16} /><span>{error}</span></div>
            )}
            <form onSubmit={handleSendResetCode}>
              <div className="form-group">
                <label>Email</label>
                <div className="input-with-icon">
                  <Mail size={18} />
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              </div>
              <button type="submit" className="btn-login" disabled={loading || !resetEmail.trim()}>
                {loading ? (
                  <><div className="loading-spinner small"></div> Sending code...</>
                ) : 'Send Verification Code'}
              </button>
            </form>
            <button onClick={handleBackToLogin} className="btn-link" style={{ marginTop: '16px', background: 'none', border: 'none', color: '#2c5f4e', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ArrowLeft size={14} /> Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== FORGOT PIN: Enter verification code =====
  if (resetStep === 'verify') {
    return (
      <div className="portal-login">
        <div className="login-container">
          <div className="login-header">
            <div className="login-logo">
              <Leaf size={32} />
              <div>
                <h1>Two Sisters</h1>
                <span>Client Portal</span>
              </div>
            </div>
          </div>
          <div className="login-card">
            <h2>Enter Code</h2>
            <p className="login-subtitle">We sent a 6-digit code to {maskEmail(resetEmail)}</p>
            {error && (
              <div className="login-error"><AlertCircle size={16} /><span>{error}</span></div>
            )}
            <form onSubmit={handleVerifyCode}>
              <div className="form-group">
                <label>Verification Code</label>
                <div className="input-with-icon">
                  <Lock size={18} />
                  <input
                    type="text"
                    placeholder="6-digit code"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                </div>
              </div>
              <button type="submit" className="btn-login" disabled={!resetCode.trim()}>
                Verify Code
              </button>
            </form>
            <button onClick={() => { setResetStep('email'); setError(null); setResetCode(''); }} className="btn-link" style={{ marginTop: '16px', background: 'none', border: 'none', color: '#2c5f4e', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ArrowLeft size={14} /> Resend code
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== TOKEN-BASED PIN RESET (from admin-sent email link) =====
  if (resetStep === 'token') {
    const handleTokenSetPin = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError(null);

      if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
        setError('PIN must be exactly 4 digits.');
        setLoading(false);
        return;
      }

      if (newPin !== confirmPin) {
        setError('PINs do not match.');
        setLoading(false);
        return;
      }

      try {
        // Save new PIN via server-side API (avoids Firestore auth requirement)
        const res = await fetch('/.netlify/functions/portal-data', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'setPortalPin',
            clientId: resetClientData.id,
            newPin,
            resetToken: resetTokenValue
          })
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to set PIN');
        }

        // Reset flow state
        setResetStep(null);
        setNewPin('');
        setConfirmPin('');
        setResetClientData(null);
        setResetTokenValue(null);
        setSuccessMessage('PIN updated! You can now sign in with your new PIN.');
      } catch (err) {
        console.error('Set PIN error:', err);
        setError('Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="portal-login">
        <div className="login-container">
          <div className="login-header">
            <div className="login-logo">
              <Leaf size={32} />
              <div>
                <h1>Two Sisters</h1>
                <span>Client Portal</span>
              </div>
            </div>
          </div>
          <div className="login-card">
            <h2>Set New PIN</h2>
            <p className="login-subtitle">Hi {resetClientData?.firstName}! Choose a new 4-digit PIN for your portal account.</p>
            {error && (
              <div className="login-error"><AlertCircle size={16} /><span>{error}</span></div>
            )}
            <form onSubmit={handleTokenSetPin}>
              <div className="form-group">
                <label>New PIN</label>
                <div className="input-with-icon">
                  <Lock size={18} />
                  <input
                    type="password"
                    placeholder="4-digit PIN"
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value)}
                    maxLength={4}
                    inputMode="numeric"
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Confirm PIN</label>
                <div className="input-with-icon">
                  <Lock size={18} />
                  <input
                    type="password"
                    placeholder="Re-enter PIN"
                    value={confirmPin}
                    onChange={(e) => setConfirmPin(e.target.value)}
                    maxLength={4}
                    inputMode="numeric"
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>
              </div>
              <button type="submit" className="btn-login" disabled={loading || !newPin.trim() || !confirmPin.trim()}>
                {loading ? (
                  <><div className="loading-spinner small"></div> Saving...</>
                ) : 'Set PIN'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ===== FORGOT PIN: Set new PIN =====
  if (resetStep === 'newpin') {
    return (
      <div className="portal-login">
        <div className="login-container">
          <div className="login-header">
            <div className="login-logo">
              <Leaf size={32} />
              <div>
                <h1>Two Sisters</h1>
                <span>Client Portal</span>
              </div>
            </div>
          </div>
          <div className="login-card">
            <h2>Set New PIN</h2>
            <p className="login-subtitle">Choose a 4-digit PIN for your account</p>
            {error && (
              <div className="login-error"><AlertCircle size={16} /><span>{error}</span></div>
            )}
            <form onSubmit={handleSetNewPin}>
              <div className="form-group">
                <label>New PIN</label>
                <div className="input-with-icon">
                  <Lock size={18} />
                  <input
                    type="password"
                    placeholder="4-digit PIN"
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value)}
                    maxLength={4}
                    inputMode="numeric"
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Confirm PIN</label>
                <div className="input-with-icon">
                  <Lock size={18} />
                  <input
                    type="password"
                    placeholder="Re-enter PIN"
                    value={confirmPin}
                    onChange={(e) => setConfirmPin(e.target.value)}
                    maxLength={4}
                    inputMode="numeric"
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>
              </div>
              <button type="submit" className="btn-login" disabled={loading || !newPin.trim() || !confirmPin.trim()}>
                {loading ? (
                  <><div className="loading-spinner small"></div> Saving...</>
                ) : 'Set PIN'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ===== DEFAULT: Login form =====
  return (
    <div className="portal-login">
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo">
            <Leaf size={32} />
            <div>
              <h1>Two Sisters</h1>
              <span>Client Portal</span>
            </div>
          </div>
        </div>

        <div className="login-card">
          <h2>Welcome Back</h2>
          <p className="login-subtitle">Sign in to access your account</p>

          {successMessage && (
            <div className="login-success" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: '#166534', fontSize: '14px' }}>
              <CheckCircle size={16} />
              <span>{successMessage}</span>
            </div>
          )}

          {(error || externalError) && (
            <div className="login-error">
              <AlertCircle size={16} />
              <span>{error || externalError}</span>
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Email</label>
              <div className="input-with-icon">
                <Mail size={18} />
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={loginInput}
                  onChange={(e) => { setLoginInput(e.target.value); setSuccessMessage(null); }}
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="form-group">
              <label>PIN</label>
              <div className="input-with-icon">
                <CreditCard size={18} />
                <input
                  type="password"
                  placeholder="4-digit PIN"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  maxLength={4}
                  inputMode="numeric"
                  autoComplete="off"
                />
              </div>
            </div>

            <button type="submit" className="btn-login" disabled={loading || !loginInput.trim() || !password.trim()}>
              {loading ? (
                <><div className="loading-spinner small"></div> Signing in...</>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <button onClick={() => { setResetStep('email'); setError(null); setSuccessMessage(null); }} className="btn-link" style={{ marginTop: '16px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '14px', width: '100%', textAlign: 'center' }}>
            Forgot PIN?
          </button>
        </div>

        <div className="login-footer">
          <p>Need help? Contact us:</p>
          <a href="tel:+14342072246"><Phone size={14} /> (434) 207-2246</a>
        </div>
      </div>
    </div>
  );
}

// ===== MAIN PORTAL COMPONENT =====
function ClientPortalMain() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { 
    state, 
    getClientById, 
    getEstimateByToken, 
    updateInvoice, 
    dispatch,
    addMessage,
    addReply,
    getClientMessages,
    getClientUnreadCount,
    markMessageReadByClient
  } = useApp();

  // Portal state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [clientEmail, setClientEmail] = useState(null);
  const [loggedInClient, setLoggedInClient] = useState(null); // Store client from portal login
  const initialTab = searchParams.get('tab') || 'home';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showAddOnModal, setShowAddOnModal] = useState(false);
  const [showLaundryModal, setShowLaundryModal] = useState(false);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [showBookCleaningModal, setShowBookCleaningModal] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loginError, setLoginError] = useState(null);
  const [tipModal, setTipModal] = useState(null); // { invoice, tipAmount, customTip }
  const [tipLoading, setTipLoading] = useState(false);
  const [photoModalJob, setPhotoModalJob] = useState(null); // Job to show photos for

  // Change PIN state (account settings)
  const [showChangePin, setShowChangePin] = useState(false);
  const [changePinStep, setChangePinStep] = useState('current'); // 'current' | 'verify' | 'newpin'
  const [changePinCode, setChangePinCode] = useState('');
  const [changePinNew, setChangePinNew] = useState('');
  const [changePinConfirm, setChangePinConfirm] = useState('');
  const [changePinGeneratedCode, setChangePinGeneratedCode] = useState(null);
  const [changePinCodeExpires, setChangePinCodeExpires] = useState(null);
  const [changePinLoading, setChangePinLoading] = useState(false);
  const [changePinError, setChangePinError] = useState(null);
  const [changePinSuccess, setChangePinSuccess] = useState(null);

  // Portal-specific data loaded directly from Firestore (independent of admin auth)
  const [portalJobs, setPortalJobs] = useState([]);
  const [portalInvoices, setPortalInvoices] = useState([]);
  const [portalProperties, setPortalProperties] = useState([]);
  const [portalMessages, setPortalMessages] = useState([]);
  const [portalDataLoaded, setPortalDataLoaded] = useState(false);

  // Check for token-based auth (estimate approval link)
  const estimate = token ? getEstimateByToken(token) : null;
  
  // Find client by email or from estimate, or use logged in client
  const client = useMemo(() => {
    // If we have a logged in client from portal login, use that
    if (loggedInClient) {
      return loggedInClient;
    }
    if (estimate) {
      return getClientById(estimate.clientId);
    }
    if (clientEmail) {
      return state.clients.find(c =>
        c.email?.toLowerCase() === clientEmail.toLowerCase()
      );
    }
    return null;
  }, [loggedInClient, estimate, clientEmail, state.clients, getClientById]);

  // Use portal-loaded data if available, fall back to AppContext state
  // Portal data takes priority because it's specific to this client
  const effectiveJobs = useMemo(() => {
    return portalJobs.length > 0 ? portalJobs : state.jobs;
  }, [portalJobs, state.jobs]);
  
  const effectiveInvoices = useMemo(() => {
    return portalInvoices.length > 0 ? portalInvoices : state.invoices;
  }, [portalInvoices, state.invoices]);
  
  const effectiveProperties = useMemo(() => {
    return portalProperties.length > 0 ? portalProperties : state.properties;
  }, [portalProperties, state.properties]);
  
  const effectiveMessages = useMemo(() => {
    return portalMessages.length > 0 ? portalMessages : state.messages;
  }, [portalMessages, state.messages]);

  // Get client's upcoming jobs
  const upcomingJobs = useMemo(() => {
    if (!client) return [];
    return effectiveJobs
      .filter(j => j.clientId === client.id && j.status !== 'cancelled')
      .filter(j => {
        const jobDate = parseISO(j.date);
        return isFuture(jobDate) || isToday(jobDate);
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);
  }, [client, effectiveJobs]);

  // Get client's past jobs
  const pastJobs = useMemo(() => {
    if (!client) return [];
    return effectiveJobs
      .filter(j => j.clientId === client.id && j.status !== 'cancelled')
      .filter(j => {
        const jobDate = parseISO(j.date);
        return isPast(jobDate) && !isToday(jobDate);
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 10);
  }, [client, effectiveJobs]);

  // Get client's invoices (exclude drafts and cancelled)
  const clientInvoices = useMemo(() => {
    if (!client) return [];
    return effectiveInvoices
      .filter(i => i.clientId === client.id && i.status !== 'draft' && i.status !== 'cancelled')
      .map(inv => {
        // Auto-detect overdue
        if ((inv.status === 'pending' || inv.status === 'sent') && inv.dueDate) {
          const dueDate = new Date(inv.dueDate);
          dueDate.setHours(23, 59, 59);
          if (new Date() > dueDate) {
            return { ...inv, displayStatus: 'overdue' };
          }
        }
        return { ...inv, displayStatus: inv.status };
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [client, effectiveInvoices]);

  // Unpaid invoices (pending, sent, or overdue)
  const unpaidInvoices = useMemo(() => {
    return clientInvoices.filter(i => 
      i.status === 'pending' || i.status === 'sent' || i.displayStatus === 'overdue'
    );
  }, [clientInvoices]);

  // Get client's property
  const property = useMemo(() => {
    if (!client) return null;
    return effectiveProperties.find(p => p.clientId === client.id);
  }, [client, effectiveProperties]);

  // Get client's messages - use AppContext if available, otherwise portal data
  const clientMessages = useMemo(() => {
    if (!client) return [];
    // Try AppContext first (admin logged in)
    const appMessages = getClientMessages(client.id);
    if (appMessages.length > 0) return appMessages;
    // Fall back to portal-loaded messages
    return effectiveMessages
      .filter(m => m.clientId === client.id)
      .sort((a, b) => new Date(b.lastMessageAt || b.createdAt) - new Date(a.lastMessageAt || a.createdAt));
  }, [client, state.messages, effectiveMessages]);

  // Get unread message count for client
  const unreadMessageCount = useMemo(() => {
    if (!client) return 0;
    // Try AppContext first
    const appCount = getClientUnreadCount(client.id);
    if (appCount > 0) return appCount;
    // Fall back to manual count from portal messages
    return effectiveMessages.filter(m => 
      m.clientId === client.id && 
      m.lastAdminMessageAt && 
      (!m.lastClientReadAt || new Date(m.lastAdminMessageAt) > new Date(m.lastClientReadAt))
    ).length;
  }, [client, state.messages, effectiveMessages]);

  // Auto-authenticate if coming from estimate link
  useEffect(() => {
    if (estimate && client) {
      setIsAuthenticated(true);
    }
  }, [estimate, client]);

  // Load portal data directly from Firestore using real-time listeners
  // Falls back to one-time reads if listeners fail (permission issues)
  useEffect(() => {
    if (!client?.id) return;
    
    console.log('Setting up Firestore listeners for client:', client.id);
    
    const unsubscribers = [];
    let listenersFailed = false;
    
    // Fallback: one-time reads using getDocs
    const loadDataOnce = async () => {
      try {
        console.log('Using one-time getDocs for client:', client.id);
        const [jobsSnap, invoicesSnap, propsSnap, msgsSnap] = await Promise.all([
          getDocs(query(collection(db, 'jobs'), where('clientId', '==', client.id))),
          getDocs(query(collection(db, 'invoices'), where('clientId', '==', client.id))),
          getDocs(query(collection(db, 'properties'), where('clientId', '==', client.id))),
          getDocs(query(collection(db, 'messages'), where('clientId', '==', client.id)))
        ]);
        
        setPortalJobs(jobsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setPortalInvoices(invoicesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setPortalProperties(propsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setPortalMessages(msgsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        
        console.log(`Portal data loaded via getDocs: ${jobsSnap.size} jobs, ${invoicesSnap.size} invoices`);
        setPortalDataLoaded(true);
      } catch (err) {
        console.error('getDocs fallback also failed:', err);
        setPortalDataLoaded(true);
      }
    };
    
    // Try real-time listeners first
    const handleListenerError = (collectionName) => (err) => {
      console.error(`${collectionName} listener error:`, err.code, err.message);
      if (!listenersFailed) {
        listenersFailed = true;
        // Clean up failed listeners
        unsubscribers.forEach(unsub => { try { unsub(); } catch(e) {} });
        // Fall back to one-time reads
        loadDataOnce();
      }
    };
    
    try {
      // Jobs listener
      const jobsQuery = query(collection(db, 'jobs'), where('clientId', '==', client.id));
      unsubscribers.push(onSnapshot(jobsQuery, (snap) => {
        const jobs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPortalJobs(jobs);
        setPortalDataLoaded(true);
        console.log('Portal jobs loaded:', jobs.length);
      }, handleListenerError('Jobs')));
      
      // Invoices listener
      const invoicesQuery = query(collection(db, 'invoices'), where('clientId', '==', client.id));
      unsubscribers.push(onSnapshot(invoicesQuery, (snap) => {
        const invoices = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPortalInvoices(invoices);
        console.log('Portal invoices loaded:', invoices.length);
      }, handleListenerError('Invoices')));
      
      // Properties listener
      const propsQuery = query(collection(db, 'properties'), where('clientId', '==', client.id));
      unsubscribers.push(onSnapshot(propsQuery, (snap) => {
        const props = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPortalProperties(props);
      }, handleListenerError('Properties')));
      
      // Messages listener
      const msgsQuery = query(collection(db, 'messages'), where('clientId', '==', client.id));
      unsubscribers.push(onSnapshot(msgsQuery, (snap) => {
        const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPortalMessages(msgs);
      }, handleListenerError('Messages')));
    } catch (err) {
      console.error('Error setting up listeners:', err);
      loadDataOnce();
    }
    
    return () => {
      unsubscribers.forEach(unsub => { try { unsub(); } catch(e) {} });
    };
  }, [client?.id]);

  // Handle login - now receives client object directly from PortalLogin
  const handleLogin = (clientData) => {
    console.log('handleLogin called with:', clientData);
    if (clientData) {
      setLoggedInClient(clientData); // Store the client object
      setClientEmail(clientData.email); // Store identifier for session
      setIsAuthenticated(true);
      setLoginError(null);
      console.log('Login successful, isAuthenticated set to true');
    } else {
      setLoginError('No account found with this information. Please contact us to get started.');
    }
  };

  // Handle logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    setClientEmail(null);
    setLoggedInClient(null);
    setActiveTab('home');
    // Clear remembered session
    localStorage.removeItem('ts_portal_session');
  };

  // Handle reschedule request
  const handleRescheduleRequest = (job, newDate, notes) => {
    // In production, this would create a reschedule request
    // For now, just show confirmation
    alert(`Reschedule request submitted for ${format(parseISO(job.date), 'MMM d')}. We'll contact you shortly to confirm the new date.`);
    setShowRescheduleModal(false);
    setSelectedJob(null);
  };

  // Format job time display
  const formatJobTime = (job) => {
    if (!job.scheduledTime) return 'Time TBD';
    const [hours, minutes] = job.scheduledTime.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // If not authenticated, show login
  if (!isAuthenticated) {
    // Special case: estimate approval link
    if (token && estimate) {
      // Redirect to estimate approval portal
      return <EstimateApprovalView estimate={estimate} client={client} />;
    }
    
    return <PortalLogin onLogin={handleLogin} error={loginError} />;
  }

  // If no client found
  if (!client) {
    return (
      <div className="portal-page">
        <div className="portal-error-screen">
          <AlertCircle size={48} />
          <h2>Account Not Found</h2>
          <p>We couldn't find an account with your information. Please contact us to get started.</p>
          <a href="tel:+14342072246" className="btn-primary">
            <Phone size={18} />
            Call Us
          </a>
        </div>
      </div>
    );
  }

  // Show loading while portal data is being fetched
  if (!portalDataLoaded && state.jobs.length === 0) {
    return (
      <div className="portal-page">
        <header className="portal-main-header">
          <div className="header-content">
            <div className="header-logo">
              <Leaf size={24} />
              <span>Two Sisters</span>
            </div>
            <div className="header-user">
              <span>{client.firstName}</span>
            </div>
          </div>
        </header>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', gap: '16px' }}>
          <div className="loading-spinner" style={{ width: 40, height: 40 }}></div>
          <p style={{ color: '#64748b', fontSize: '0.95rem' }}>Loading your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-page">
      {/* Opt-in prompt if completely opted out */}
      <OptInPrompt client={client} dispatch={dispatch} />
      
      {/* Header */}
      <header className="portal-main-header">
        <div className="header-content">
          <div className="header-logo">
            <Leaf size={24} />
            <span>Two Sisters</span>
          </div>
          <div className="header-user">
            <span>{client.firstName}</span>
            <button onClick={handleLogout} className="btn-logout">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="portal-nav">
        <button
          className={`nav-tab ${activeTab === 'home' ? 'active' : ''}`}
          onClick={() => setActiveTab('home')}
        >
          <Home size={18} />
          <span>Home</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'appointments' ? 'active' : ''}`}
          onClick={() => setActiveTab('appointments')}
        >
          <Calendar size={18} />
          <span>Appointments</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => setActiveTab('messages')}
        >
          <MessageSquare size={18} />
          <span>Messages</span>
          {unreadMessageCount > 0 && (
            <span className="nav-badge">{unreadMessageCount}</span>
          )}
        </button>
        <button
          className={`nav-tab ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          <FileText size={18} />
          <span>Invoices</span>
          {unpaidInvoices.length > 0 && (
            <span className="nav-badge">{unpaidInvoices.length}</span>
          )}
        </button>
        <button
          className={`nav-tab ${activeTab === 'account' ? 'active' : ''}`}
          onClick={() => setActiveTab('account')}
        >
          <User size={18} />
          <span>Account</span>
        </button>
      </nav>

      {/* Main Content */}
      <main className="portal-main">
        {/* HOME TAB */}
        {activeTab === 'home' && (
          <div className="portal-home">
            {/* Welcome Section */}
            <section className="welcome-section">
              <h1>Welcome, {client.firstName}!</h1>
              <p>Manage your appointments and account</p>
            </section>

            {/* Quick Actions */}
            <section className="quick-actions">
              <button className="action-card" onClick={() => setShowBookCleaningModal(true)}>
                <div className="action-icon booking">
                  <Plus size={24} />
                </div>
                <div className="action-text">
                  <h3>Book Extra Cleaning</h3>
                  <p>Schedule a one-time visit</p>
                </div>
                <ChevronRight size={20} />
              </button>

              <button className="action-card" onClick={() => setShowAddOnModal(true)}>
                <div className="action-icon">
                  <CalendarPlus size={24} />
                </div>
                <div className="action-text">
                  <h3>Book Add-On</h3>
                  <p>Extra services for your next visit</p>
                </div>
                <ChevronRight size={20} />
              </button>

              <button className="action-card" onClick={() => setShowLaundryModal(true)}>
                <div className="action-icon laundry">
                  <Shirt size={24} />
                </div>
                <div className="action-text">
                  <h3>Laundry Pickup</h3>
                  <p>Schedule pickup & delivery</p>
                </div>
                <ChevronRight size={20} />
              </button>

              {unpaidInvoices.length > 0 && (
                <button className="action-card highlight" onClick={() => setActiveTab('invoices')}>
                  <div className="action-icon payment">
                    <CreditCard size={24} />
                  </div>
                  <div className="action-text">
                    <h3>Pay Invoice</h3>
                    <p>{unpaidInvoices.length} invoice{unpaidInvoices.length > 1 ? 's' : ''} pending</p>
                  </div>
                  <ChevronRight size={20} />
                </button>
              )}
            </section>

            {/* Upcoming Appointments */}
            <section className="upcoming-section">
              <div className="section-header">
                <h2>Upcoming Appointments</h2>
                <button className="btn-link" onClick={() => setActiveTab('appointments')}>
                  View All
                </button>
              </div>

              {upcomingJobs.length > 0 ? (
                <div className="appointment-list">
                  {upcomingJobs.slice(0, 3).map(job => (
                    <div key={job.id} className="appointment-card">
                      <div className="appointment-date">
                        <span className="day">{format(parseISO(job.date), 'd')}</span>
                        <span className="month">{format(parseISO(job.date), 'MMM')}</span>
                      </div>
                      <div className="appointment-info">
                        <h4>{job.serviceType === 'recurring' ? 'Regular Cleaning' : 
                             job.serviceType === 'deep_clean' ? 'Deep Clean' : 
                             job.serviceType || 'Cleaning Service'}</h4>
                        <p className="appointment-time">
                          <Clock size={14} />
                          {formatJobTime(job)}
                          {job.teamNames?.length > 0 && ` • ${job.teamNames[0]}`}
                        </p>
                      </div>
                      <button
                        className="btn-reschedule"
                        onClick={() => {
                          setSelectedJob(job);
                          setShowRescheduleModal(true);
                        }}
                      >
                        Reschedule
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <Calendar size={32} />
                  <p>No upcoming appointments</p>
                  <a href="tel:+14342072246" className="btn-link">Schedule a cleaning</a>
                </div>
              )}
            </section>

            {/* Recent Activity */}
            {pastJobs.length > 0 && (
              <section className="recent-section">
                <div className="section-header">
                  <h2>Recent Visits</h2>
                </div>
                <div className="recent-list">
                  {pastJobs.slice(0, 2).map(job => (
                    <div key={job.id} className="recent-item">
                      <div className="recent-icon">
                        {job.status === 'completed' ? <CheckCircle size={18} /> : <Clock size={18} />}
                      </div>
                      <div className="recent-info">
                        <span className="recent-date">{format(parseISO(job.date), 'MMM d, yyyy')}</span>
                        <span className="recent-type">
                          {job.serviceType === 'recurring' ? 'Regular Cleaning' : 
                           job.serviceType === 'deep_clean' ? 'Deep Clean' :
                           job.serviceType || 'Cleaning Service'}
                        </span>
                      </div>
                      {job.photos?.some(p => p.type === 'before' || p.type === 'after') && (
                        <button
                          className="btn-photos"
                          onClick={() => setPhotoModalJob(job)}
                        >
                          <Camera size={16} />
                          Photos
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {/* APPOINTMENTS TAB */}
        {activeTab === 'appointments' && (
          <div className="portal-appointments">
            <h2>Your Appointments</h2>

            {/* Upcoming */}
            <section className="appointments-section">
              <h3>Upcoming</h3>
              {upcomingJobs.length > 0 ? (
                <div className="appointment-list full">
                  {upcomingJobs.map(job => (
                    <div key={job.id} className="appointment-card detailed">
                      <div className="appointment-date">
                        <span className="day">{format(parseISO(job.date), 'd')}</span>
                        <span className="month">{format(parseISO(job.date), 'MMM')}</span>
                        <span className="weekday">{format(parseISO(job.date), 'EEE')}</span>
                      </div>
                      <div className="appointment-details">
                        <h4>{job.serviceType === 'recurring' ? 'Regular Cleaning' : 
                             job.serviceType === 'deep_clean' ? 'Deep Clean' : 
                             job.serviceType || 'Cleaning Service'}</h4>
                        <p className="appointment-time">
                          <Clock size={14} />
                          {formatJobTime(job)}
                          {job.duration && ` (${job.duration} hrs)`}
                        </p>
                        <p className="appointment-address">
                          <MapPin size={14} />
                          {maskAddress(job.address || property?.addressLine1) || 'Address on file'}
                        </p>
                        {job.teamNames?.length > 0 && (
                          <p className="appointment-team">
                            <User size={14} />
                            Team: {job.teamNames.join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="appointment-actions">
                        <button
                          className="btn-outline"
                          onClick={() => {
                            setSelectedJob(job);
                            setShowRescheduleModal(true);
                          }}
                        >
                          Reschedule
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <Calendar size={32} />
                  <p>No upcoming appointments</p>
                </div>
              )}
            </section>

            {/* Past */}
            {pastJobs.length > 0 && (
              <section className="appointments-section">
                <h3>Past Visits</h3>
                <div className="appointment-list full past">
                  {pastJobs.map(job => (
                    <div key={job.id} className="appointment-card past">
                      <div className="appointment-date">
                        <span className="day">{format(parseISO(job.date), 'd')}</span>
                        <span className="month">{format(parseISO(job.date), 'MMM')}</span>
                      </div>
                      <div className="appointment-details">
                        <h4>{job.serviceType === 'recurring' ? 'Regular Cleaning' :
                             job.serviceType === 'deep_clean' ? 'Deep Clean' :
                             job.serviceType || 'Cleaning Service'}</h4>
                        <p className="completed-badge">
                          {job.status === 'completed' ? (
                            <><CheckCircle size={14} /> Completed</>
                          ) : (
                            <><Clock size={14} /> {job.status === 'scheduled' ? 'Scheduled' : job.status}</>
                          )}
                        </p>
                        {job.photos?.some(p => p.type === 'before' || p.type === 'after') && (
                          <button
                            className="btn-view-photos"
                            onClick={() => setPhotoModalJob(job)}
                          >
                            <Camera size={14} />
                            View Before & After Photos
                          </button>
                        )}
                      </div>
                      <div className="appointment-price">
                        {formatCurrency(job.price || 0)}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {/* INVOICES TAB */}
        {activeTab === 'invoices' && (
          <div className="portal-invoices">
            <h2>Invoices & Payments</h2>

            {/* Unpaid Invoices */}
            {unpaidInvoices.length > 0 && (
              <section className="invoices-section">
                <h3>Outstanding Balance</h3>
                <div className="invoice-list">
                  {unpaidInvoices.map(invoice => (
                    <div key={invoice.id} className="invoice-card unpaid">
                      <div className="invoice-header">
                        <span className="invoice-number">{invoice.invoiceNumber}</span>
                        <span className={`invoice-status ${invoice.displayStatus}`}>
                          {invoice.displayStatus === 'overdue' ? 'Overdue' : 'Due'}
                        </span>
                      </div>
                      <div className="invoice-body">
                        <div className="invoice-amount">
                          {formatCurrency(invoice.total)}
                        </div>
                        <div className="invoice-due">
                          Due {format(parseISO(invoice.dueDate), 'MMM d, yyyy')}
                        </div>
                      </div>
                      {invoice.stripePaymentUrl ? (
                        <button
                          className="btn-pay"
                          onClick={() => setTipModal({
                            invoice,
                            tipAmount: Math.round(invoice.total * 0.20 * 100) / 100,
                            customTip: ''
                          })}
                        >
                          <CreditCard size={18} />
                          Pay Now
                        </button>
                      ) : (
                        <button
                          className="btn-pay"
                          onClick={() => setTipModal({ invoice, tipAmount: Math.round(invoice.total * 0.20 * 100) / 100, customTip: '' })}
                        >
                          <CreditCard size={18} />
                          Pay Now
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* All Invoices */}
            <section className="invoices-section">
              <h3>Invoice History</h3>
              {clientInvoices.length > 0 ? (
                <div className="invoice-history">
                  {clientInvoices.map(invoice => (
                    <div 
                      key={invoice.id} 
                      className={`invoice-row ${invoice.displayStatus !== 'paid' ? 'clickable' : ''}`}
                      onClick={() => {
                        if (invoice.displayStatus !== 'paid') {
                          setTipModal({ invoice, tipAmount: Math.round(invoice.total * 0.20 * 100) / 100, customTip: '' });
                        }
                      }}
                    >
                      <div className="invoice-row-info">
                        <span className="invoice-number">{invoice.invoiceNumber}</span>
                        <span className="invoice-date">
                          {format(parseISO(invoice.createdAt), 'MMM d, yyyy')}
                        </span>
                      </div>
                      <div className="invoice-row-amount">
                        {formatCurrency(invoice.total)}
                      </div>
                      <div className={`invoice-row-status ${invoice.displayStatus}`}>
                        {invoice.displayStatus === 'paid' && <CheckCircle size={14} />}
                        {(invoice.displayStatus === 'pending' || invoice.displayStatus === 'sent') && <Clock size={14} />}
                        {invoice.displayStatus === 'overdue' && <AlertCircle size={14} />}
                        <span>
                          {invoice.displayStatus === 'sent' ? 'Pending' : 
                           invoice.displayStatus === 'overdue' ? 'Overdue' :
                           invoice.displayStatus === 'paid' ? 'Paid' : 
                           invoice.displayStatus.charAt(0).toUpperCase() + invoice.displayStatus.slice(1)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <FileText size={32} />
                  <p>No invoices yet</p>
                </div>
              )}
            </section>

            {/* Tip Modal */}
            {tipModal && (
              <div className="tip-modal-overlay" onClick={() => !tipLoading && setTipModal(null)}>
                <div className="tip-modal" onClick={e => e.stopPropagation()}>
                  <button className="tip-modal-close" onClick={() => !tipLoading && setTipModal(null)}>&times;</button>
                  <div className="tip-modal-header">
                    <h3>Add a Tip?</h3>
                    <p>Tips go directly to your cleaning team and are always appreciated!</p>
                  </div>

                  <div className="tip-modal-invoice-info">
                    <span>Invoice {tipModal.invoice.invoiceNumber}</span>
                    <span className="tip-invoice-total">{formatCurrency(tipModal.invoice.total)}</span>
                  </div>

                  <div className="tip-options">
                    {[
                      { label: '15%', value: Math.round(tipModal.invoice.total * 0.15 * 100) / 100 },
                      { label: '20%', value: Math.round(tipModal.invoice.total * 0.20 * 100) / 100 },
                      { label: '25%', value: Math.round(tipModal.invoice.total * 0.25 * 100) / 100 },
                    ].map(opt => (
                      <button
                        key={opt.label}
                        className={`tip-option ${tipModal.tipAmount === opt.value ? 'selected' : ''}`}
                        onClick={() => setTipModal(prev => ({ ...prev, tipAmount: opt.value, customTip: '' }))}
                      >
                        <span className="tip-label">{opt.label}</span>
                        <span className="tip-value">{formatCurrency(opt.value)}</span>
                      </button>
                    ))}
                    <button
                      className={`tip-option ${tipModal.tipAmount === 'custom' ? 'selected' : ''}`}
                      onClick={() => setTipModal(prev => ({ ...prev, tipAmount: 'custom' }))}
                    >
                      <span className="tip-label">Custom</span>
                    </button>
                  </div>
                  <button
                    className="tip-skip-link"
                    onClick={() => setTipModal(prev => ({ ...prev, tipAmount: 0, customTip: '' }))}
                  >
                    No tip this time
                  </button>

                  {tipModal.tipAmount === 'custom' && (
                    <div className="tip-custom-input">
                      <span className="tip-dollar">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={tipModal.customTip}
                        onChange={e => setTipModal(prev => ({ ...prev, customTip: e.target.value }))}
                        autoFocus
                      />
                    </div>
                  )}

                  {tipModal.tipAmount !== null && (
                    <div className="tip-total">
                      <span>Total with tip:</span>
                      <span className="tip-total-amount">
                        {formatCurrency(
                          tipModal.invoice.total + (
                            tipModal.tipAmount === 'custom' 
                              ? (parseFloat(tipModal.customTip) || 0) 
                              : (tipModal.tipAmount || 0)
                          )
                        )}
                      </span>
                    </div>
                  )}

                  <button
                    className="tip-pay-btn"
                    disabled={tipModal.tipAmount === null || tipLoading}
                    onClick={async () => {
                      const tipDollars = tipModal.tipAmount === 'custom' 
                        ? (parseFloat(tipModal.customTip) || 0) 
                        : (tipModal.tipAmount || 0);
                      const tipCents = Math.round(tipDollars * 100);
                      
                      setTipLoading(true);
                      try {
                        const inv = tipModal.invoice;
                        
                        // If invoice already has a Stripe payment URL and no tip, just redirect
                        if (inv.stripePaymentUrl && tipDollars === 0) {
                          window.open(inv.stripePaymentUrl, '_blank');
                          setTipModal(null);
                          setTipLoading(false);
                          return;
                        }

                        // If invoice already has a Stripe invoice and user wants to add a tip,
                        // use the sendInvoiceWithTip flow instead of creating a brand new invoice
                        if (inv.stripeInvoiceId && tipDollars > 0) {
                          // Save tip split info
                          const linkedJob = effectiveJobs.find(j => j.id === inv.jobId);
                          let tipSplit = [];
                          if (linkedJob?.team?.length > 0) {
                            const splitAmount = Math.round((tipDollars / linkedJob.team.length) * 100) / 100;
                            tipSplit = linkedJob.team.map(empId => ({
                              employeeId: empId,
                              amount: splitAmount
                            }));
                          }
                          try {
                            await updateDoc(doc(db, 'invoices', inv.id), {
                              tipAmount: tipDollars,
                              tipSplit,
                              tipSource: 'stripe'
                            });
                          } catch (e) {
                            console.error('Error saving tip split:', e);
                          }
                          // Add tip to existing Stripe invoice
                          let stripeCustomerId = client.stripeCustomerId;
                          if (!stripeCustomerId) {
                            // Edge case: shouldn't happen if invoice exists, but handle it
                            throw new Error('Missing payment profile. Please contact us.');
                          }
                          const tipResp = await fetch('/.netlify/functions/stripe-invoice', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              action: 'sendInvoiceWithTip',
                              data: {
                                invoiceId: inv.stripeInvoiceId,
                                customerId: stripeCustomerId,
                                tipAmountCents: tipCents
                              }
                            })
                          });
                          const tipResult = await tipResp.json();
                          if (tipResult.success && tipResult.hostedUrl) {
                            window.open(tipResult.hostedUrl, '_blank');
                          } else if (tipResult.success && tipResult.tipInvoiceUrl) {
                            // Tip went to a separate invoice — open the main one
                            window.open(inv.stripePaymentUrl || tipResult.tipInvoiceUrl, '_blank');
                          } else {
                            throw new Error(tipResult.error || 'Failed to add tip');
                          }
                          setTipModal(null);
                          setTipLoading(false);
                          return;
                        }

                        // Build line items from our invoice
                        const lineItems = (inv.lineItems || []).map(item => ({
                          description: item.description,
                          amount: Math.round((item.total || item.amount || 0) * 100),
                          quantity: item.quantity || 1
                        }));

                        // If no line items (shouldn't happen), create one from total
                        if (lineItems.length === 0) {
                          lineItems.push({
                            description: `Cleaning Service - Invoice #${inv.invoiceNumber}`,
                            amount: Math.round(inv.total * 100),
                            quantity: 1
                          });
                        }

                        // Ensure client has a Stripe customer ID
                        let stripeCustomerId = client.stripeCustomerId;
                        if (!stripeCustomerId) {
                          console.log('No Stripe customer ID — creating one for portal payment');
                          const custResp = await fetch('/.netlify/functions/portal-data', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              action: 'createCustomer',
                              invoiceData: {
                                name: `${client.firstName || ''} ${client.lastName || ''}`.trim(),
                                email: client.email,
                                phone: client.phone,
                                clientId: client.id
                              }
                            })
                          });
                          const custResult = await custResp.json();
                          if (!custResult.success || !custResult.customerId) {
                            throw new Error('Failed to create payment profile. Please contact us.');
                          }
                          stripeCustomerId = custResult.customerId;
                          // Save to Firestore so future payments don't need this step
                          try {
                            await updateDoc(doc(db, 'clients', client.id), { stripeCustomerId });
                          } catch (e) {
                            console.error('Could not save Stripe customer ID to client:', e);
                          }
                        }

                        // Create Stripe invoice NOW with tip included
                        const response = await fetch('/.netlify/functions/portal-data', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            action: 'createPayment',
                            invoiceData: {
                              stripeCustomerId,
                              lineItems,
                              tipAmountCents: tipCents,
                              invoiceNumber: inv.invoiceNumber,
                              dueDate: inv.dueDate,
                              internalInvoiceId: inv.id,
                              jobId: inv.jobId,
                              isDeposit: false
                            }
                          })
                        });

                        const result = await response.json();

                        if (result.success && result.hostedUrl) {
                          // Stripe invoice created successfully — NOW save tip split to Firestore
                          // (saving after Stripe succeeds prevents orphaned tip data)
                          try {
                            const fsUpdate = {
                              stripeInvoiceId: result.stripeInvoiceId,
                              stripePaymentUrl: result.hostedUrl,
                              status: 'pending'
                            };
                            if (tipCents > 0) {
                              fsUpdate.tipAmount = tipCents / 100;
                              fsUpdate.tipSource = 'stripe';

                              // Calculate and save tip split
                              const linkedJob = effectiveJobs.find(j => j.id === inv.jobId);
                              if (linkedJob?.team?.length > 0) {
                                const splitAmount = Math.round((tipDollars / linkedJob.team.length) * 100) / 100;
                                fsUpdate.tipSplit = linkedJob.team.map(empId => ({
                                  employeeId: empId,
                                  amount: splitAmount
                                }));
                              }
                            }
                            await updateDoc(doc(db, 'invoices', inv.id), fsUpdate);
                            // Also save via backend (redundant but safe)
                            if (tipCents > 0) {
                              fetch('/.netlify/functions/portal-data', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  action: 'saveTip',
                                  invoiceId: inv.id,
                                  tipData: { tipAmount: tipCents / 100, tipSplit: fsUpdate.tipSplit || [] }
                                })
                              }).catch(() => {}); // fire and forget
                            }
                          } catch (fsErr) {
                            console.error('Client-side Firestore update failed:', fsErr);
                          }
                          window.open(result.hostedUrl, '_blank');
                        } else {
                          throw new Error(result.error || result.details || 'Failed to create payment');
                        }
                        
                        setTipModal(null);
                      } catch (err) {
                        console.error('Error creating payment:', err);
                        alert('There was an error setting up payment. Please try again or contact us.');
                      } finally {
                        setTipLoading(false);
                      }
                    }}
                  >
                    {tipLoading ? 'Processing...' : (
                      tipModal.tipAmount === 0 
                        ? 'Continue to Payment' 
                        : tipModal.tipAmount !== null 
                          ? 'Continue to Payment' 
                          : 'Select a tip option'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* MESSAGES TAB */}
        {activeTab === 'messages' && (
          <div className="portal-messages">
            <div className="messages-header-portal">
              <h2>Messages</h2>
              <button 
                className="btn-new-message"
                onClick={() => setShowNewMessageModal(true)}
              >
                <Plus size={18} />
                New Message
              </button>
            </div>

            {selectedConversation ? (
              // Conversation View
              <PortalConversationView
                conversation={selectedConversation}
                client={client}
                onBack={() => setSelectedConversation(null)}
                onReply={async (body) => {
                  await addReply(selectedConversation.id, {
                    body,
                    sender: 'client',
                    senderName: `${client.firstName} ${client.lastName}`.trim(),
                    senderEmail: client.email
                  });
                  // Send notification to admin
                  try {
                    await fetch('/.netlify/functions/send-email', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        type: 'client_message_notification',
                        data: {
                          clientName: `${client.firstName} ${client.lastName}`.trim(),
                          clientEmail: client.email,
                          clientPhone: client.phone,
                          subject: selectedConversation.subject,
                          message: body
                        }
                      })
                    });
                  } catch (err) {
                    console.error('Failed to send notification:', err);
                  }
                }}
                markAsRead={() => markMessageReadByClient(selectedConversation.id)}
              />
            ) : (
              // Message List
              <div className="portal-message-list">
                {clientMessages.length === 0 ? (
                  <div className="empty-state">
                    <Inbox size={48} />
                    <h3>No messages yet</h3>
                    <p>Send us a message and we'll get back to you soon!</p>
                    <button 
                      className="btn-primary"
                      onClick={() => setShowNewMessageModal(true)}
                    >
                      <Plus size={18} />
                      Send a Message
                    </button>
                  </div>
                ) : (
                  clientMessages.map(message => {
                    const hasUnread = message.lastAdminMessageAt && 
                      (!message.lastClientReadAt || 
                       new Date(message.lastAdminMessageAt) > new Date(message.lastClientReadAt));
                    
                    return (
                      <div
                        key={message.id}
                        className={`portal-message-card ${hasUnread ? 'unread' : ''}`}
                        onClick={() => setSelectedConversation(message)}
                      >
                        <div className="message-card-content">
                          <div className="message-card-top">
                            <span className="message-subject">{message.subject}</span>
                            <span className="message-time">
                              {formatDistanceToNow(parseISO(message.lastMessageAt), { addSuffix: true })}
                            </span>
                          </div>
                          <p className="message-preview-text">
                            {message.thread?.[message.thread.length - 1]?.body?.substring(0, 100) || ''}
                            {message.thread?.[message.thread.length - 1]?.body?.length > 100 ? '...' : ''}
                          </p>
                          <div className="message-card-bottom">
                            {message.status === 'resolved' ? (
                              <span className="status-resolved">
                                <CheckCircle size={14} />
                                Resolved
                              </span>
                            ) : hasUnread ? (
                              <span className="status-unread">New reply</span>
                            ) : (
                              <span className="status-pending">
                                {message.thread?.length || 1} message{message.thread?.length !== 1 ? 's' : ''}
                              </span>
                            )}
                            <ChevronRight size={18} />
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}

        {/* ACCOUNT TAB */}
        {activeTab === 'account' && (
          <div className="portal-account">
            <h2>Account Settings</h2>

            <section className="account-section">
              <h3>Contact Information</h3>
              <div className="info-card">
                <div className="info-row">
                  <label>Name</label>
                  <span>{client.firstName} {client.lastName}</span>
                </div>
                <div className="info-row">
                  <label>Email</label>
                  <span>{client.email || 'Not provided'}</span>
                </div>
                <div className="info-row">
                  <label>Phone</label>
                  <span>{maskPhone(client.phone)}</span>
                </div>
              </div>
            </section>

            {property && (
              <section className="account-section">
                <h3>Service Address</h3>
                <div className="info-card">
                  <div className="info-row">
                    <label>Address</label>
                    <span>{maskAddress(property.addressLine1)}</span>
                  </div>
                  <div className="info-row">
                    <label>City</label>
                    <span>{property.city}, {property.state}</span>
                  </div>
                </div>
              </section>
            )}

            <section className="account-section">
              <h3>Service Plan</h3>
              <div className="info-card">
                <div className="info-row">
                  <label>Frequency</label>
                  <span className="plan-badge">
                    {client.frequency === 'weekly' ? 'Weekly' :
                     client.frequency === 'biweekly' ? 'Bi-Weekly' :
                     client.frequency === 'monthly' ? 'Monthly' : 'On Request'}
                  </span>
                </div>
              </div>
            </section>

            {/* Communication Preferences */}
            <CommunicationPreferences 
              client={client} 
              dispatch={dispatch}
            />

            <section className="account-section">
              <h3>Security</h3>
              {changePinSuccess && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 16px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: '#166534', fontSize: '14px' }}>
                  <CheckCircle size={16} />
                  <span>{changePinSuccess}</span>
                </div>
              )}
              {!showChangePin ? (
                <button
                  onClick={() => { setShowChangePin(true); setChangePinStep('current'); setChangePinError(null); setChangePinSuccess(null); }}
                  className="help-btn"
                  style={{ width: '100%', justifyContent: 'center', gap: '8px' }}
                >
                  <Lock size={18} />
                  Change PIN
                </button>
              ) : (
                <div className="info-card" style={{ padding: '20px' }}>
                  {changePinError && (
                    <div className="login-error" style={{ marginBottom: '12px' }}><AlertCircle size={16} /><span>{changePinError}</span></div>
                  )}

                  {changePinStep === 'current' && (
                    <>
                      <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '14px' }}>We'll send a verification code to your email to confirm it's you.</p>
                      <button
                        className="btn-login"
                        disabled={changePinLoading}
                        onClick={async () => {
                          setChangePinLoading(true);
                          setChangePinError(null);
                          try {
                            const code = String(Math.floor(100000 + Math.random() * 900000));
                            setChangePinGeneratedCode(code);
                            setChangePinCodeExpires(Date.now() + (10 * 60 * 1000));
                            await sendLoginCode({ channel: 'email', email: client.email, code, clientName: client.firstName });
                            setChangePinStep('verify');
                          } catch (err) {
                            console.error('Send code error:', err);
                            setChangePinError('Failed to send code. Please try again.');
                          } finally {
                            setChangePinLoading(false);
                          }
                        }}
                      >
                        {changePinLoading ? <><div className="loading-spinner small"></div> Sending...</> : 'Send Verification Code'}
                      </button>
                    </>
                  )}

                  {changePinStep === 'verify' && (
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      setChangePinError(null);
                      const code = changePinCode.trim();
                      if (code.length !== 6 || !/^\d{6}$/.test(code)) {
                        setChangePinError('Please enter the 6-digit code.');
                        return;
                      }
                      if (Date.now() > changePinCodeExpires) {
                        setChangePinError('Code has expired. Please try again.');
                        setChangePinStep('current');
                        return;
                      }
                      if (code !== changePinGeneratedCode) {
                        setChangePinError('Incorrect code.');
                        return;
                      }
                      setChangePinStep('newpin');
                    }}>
                      <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '14px' }}>Enter the 6-digit code sent to {maskEmail(client.email)}</p>
                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <div className="input-with-icon">
                          <Lock size={18} />
                          <input type="text" placeholder="6-digit code" value={changePinCode} onChange={(e) => setChangePinCode(e.target.value)} maxLength={6} inputMode="numeric" autoComplete="one-time-code" />
                        </div>
                      </div>
                      <button type="submit" className="btn-login">Verify</button>
                    </form>
                  )}

                  {changePinStep === 'newpin' && (
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      setChangePinError(null);
                      if (changePinNew.length !== 4 || !/^\d{4}$/.test(changePinNew)) {
                        setChangePinError('PIN must be exactly 4 digits.');
                        return;
                      }
                      if (changePinNew !== changePinConfirm) {
                        setChangePinError('PINs do not match.');
                        return;
                      }
                      setChangePinLoading(true);
                      try {
                        const res = await fetch('/.netlify/functions/portal-data', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            action: 'setPortalPin',
                            clientId: client.id,
                            newPin: changePinNew
                          })
                        });
                        if (!res.ok) {
                          const data = await res.json();
                          throw new Error(data.error || 'Failed to update PIN');
                        }
                        setShowChangePin(false);
                        setChangePinStep('current');
                        setChangePinCode('');
                        setChangePinNew('');
                        setChangePinConfirm('');
                        setChangePinSuccess('PIN updated successfully!');
                      } catch (err) {
                        console.error('Update PIN error:', err);
                        setChangePinError('Failed to update PIN. Please try again.');
                      } finally {
                        setChangePinLoading(false);
                      }
                    }}>
                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label>New PIN</label>
                        <div className="input-with-icon">
                          <Lock size={18} />
                          <input type="password" placeholder="4-digit PIN" value={changePinNew} onChange={(e) => setChangePinNew(e.target.value)} maxLength={4} inputMode="numeric" autoComplete="off" disabled={changePinLoading} />
                        </div>
                      </div>
                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label>Confirm PIN</label>
                        <div className="input-with-icon">
                          <Lock size={18} />
                          <input type="password" placeholder="Re-enter PIN" value={changePinConfirm} onChange={(e) => setChangePinConfirm(e.target.value)} maxLength={4} inputMode="numeric" autoComplete="off" disabled={changePinLoading} />
                        </div>
                      </div>
                      <button type="submit" className="btn-login" disabled={changePinLoading || !changePinNew || !changePinConfirm}>
                        {changePinLoading ? <><div className="loading-spinner small"></div> Saving...</> : 'Update PIN'}
                      </button>
                    </form>
                  )}

                  <button onClick={() => { setShowChangePin(false); setChangePinStep('current'); setChangePinCode(''); setChangePinNew(''); setChangePinConfirm(''); setChangePinError(null); }} style={{ marginTop: '12px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '14px' }}>
                    Cancel
                  </button>
                </div>
              )}
            </section>

            <section className="account-section">
              <h3>Need Help?</h3>
              <div className="help-buttons">
                <a href="tel:+14342072246" className="help-btn">
                  <Phone size={18} />
                  Call Us
                </a>
                <a href="mailto:hello@twosisterscleaningco.com" className="help-btn">
                  <Mail size={18} />
                  Email Us
                </a>
              </div>
            </section>

            <button onClick={handleLogout} className="btn-logout-full">
              <LogOut size={18} />
              Sign Out
            </button>
          </div>
        )}
      </main>

      {/* RESCHEDULE MODAL */}
      {showRescheduleModal && selectedJob && (
        <RescheduleModal
          job={selectedJob}
          client={client}
          addMessage={addMessage}
          onClose={() => {
            setShowRescheduleModal(false);
            setSelectedJob(null);
          }}
        />
      )}

      {/* ADD-ON MODAL */}
      {showAddOnModal && (
        <AddOnModal
          onClose={() => setShowAddOnModal(false)}
          nextJob={upcomingJobs[0]}
          client={client}
          addMessage={addMessage}
        />
      )}

      {/* LAUNDRY MODAL */}
      {showLaundryModal && (
        <LaundryModal
          onClose={() => setShowLaundryModal(false)}
          client={client}
          addMessage={addMessage}
        />
      )}

      {/* BOOK EXTRA CLEANING MODAL */}
      {showBookCleaningModal && (
        <BookCleaningModal
          onClose={() => setShowBookCleaningModal(false)}
          client={client}
          property={property}
          addMessage={addMessage}
        />
      )}

      {/* NEW MESSAGE MODAL */}
      {showNewMessageModal && (
        <NewMessageModal
          onClose={() => setShowNewMessageModal(false)}
          client={client}
          addMessage={addMessage}
        />
      )}

      {/* BEFORE & AFTER PHOTOS MODAL */}
      {photoModalJob && (
        <BeforeAfterPhotoModal
          job={photoModalJob}
          onClose={() => setPhotoModalJob(null)}
        />
      )}
    </div>
  );
}

// ===== BEFORE & AFTER PHOTO MODAL =====
function BeforeAfterPhotoModal({ job, onClose }) {
  const [activePhotoTab, setActivePhotoTab] = useState('before');
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const beforePhotos = (job.photos || []).filter(p => p.type === 'before');
  const afterPhotos = (job.photos || []).filter(p => p.type === 'after');

  const serviceLabel = job.serviceType === 'recurring' ? 'Regular Cleaning' :
    job.serviceType === 'deep_clean' ? 'Deep Clean' :
    job.serviceType || 'Cleaning Service';

  const activePhotos = activePhotoTab === 'before' ? beforePhotos : afterPhotos;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content photo-modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>Before & After Photos</h3>
            <p className="photo-modal-subtitle">
              {serviceLabel} — {format(parseISO(job.date), 'MMMM d, yyyy')}
            </p>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className="photo-modal-body">
          {/* Tab switcher */}
          <div className="photo-tabs">
            <button
              className={`photo-tab ${activePhotoTab === 'before' ? 'active' : ''}`}
              onClick={() => setActivePhotoTab('before')}
            >
              Before {beforePhotos.length > 0 && <span className="photo-count">{beforePhotos.length}</span>}
            </button>
            <button
              className={`photo-tab ${activePhotoTab === 'after' ? 'active' : ''}`}
              onClick={() => setActivePhotoTab('after')}
            >
              After {afterPhotos.length > 0 && <span className="photo-count">{afterPhotos.length}</span>}
            </button>
          </div>

          {/* Photo grid */}
          {activePhotos.length > 0 ? (
            <div className="photo-grid">
              {activePhotos.map(photo => (
                <div
                  key={photo.id}
                  className="photo-grid-item"
                  onClick={() => setSelectedPhoto(photo)}
                >
                  <img src={photo.url} alt={`${activePhotoTab} photo`} loading="lazy" />
                </div>
              ))}
            </div>
          ) : (
            <div className="photo-empty">
              <Camera size={28} />
              <p>No {activePhotoTab} photos for this visit</p>
            </div>
          )}
        </div>
      </div>

      {/* Full-size photo overlay */}
      {selectedPhoto && (
        <div className="photo-lightbox" onClick={() => setSelectedPhoto(null)}>
          <button className="lightbox-close" onClick={() => setSelectedPhoto(null)}>
            <X size={24} />
          </button>
          <img src={selectedPhoto.url} alt="Full size" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

// ===== RESCHEDULE MODAL =====
function RescheduleModal({ job, client, addMessage, onClose }) {
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('flexible');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    const timeLabels = {
      flexible: 'Flexible',
      morning: 'Morning (8am - 12pm)',
      afternoon: 'Afternoon (12pm - 5pm)'
    };
    
    // Create message
    const messageBody = `I would like to reschedule my appointment.\n\nCurrent Date: ${format(parseISO(job.date), 'EEEE, MMMM d, yyyy')}\nPreferred New Date: ${format(parseISO(preferredDate), 'EEEE, MMMM d, yyyy')}\nPreferred Time: ${timeLabels[preferredTime]}${notes ? `\n\nAdditional Notes: ${notes}` : ''}`;
    
    try {
      await addMessage({
        clientId: client.id,
        subject: `Reschedule request for ${format(parseISO(job.date), 'MMM d')}`,
        body: messageBody,
        type: 'reschedule',
        source: 'reschedule_modal',
        sender: 'client',
        senderName: `${client.firstName} ${client.lastName}`.trim(),
        senderEmail: client.email,
        relatedJobId: job.id
      });
      
      // Send notification email to admin
      await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'client_message_notification',
          data: {
            clientName: `${client.firstName} ${client.lastName}`.trim(),
            clientEmail: client.email,
            clientPhone: client.phone,
            subject: `Reschedule request for ${format(parseISO(job.date), 'MMM d')}`,
            message: messageBody
          }
        })
      });
      
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit reschedule request:', error);
      alert('Failed to submit request. Please try again.');
    }
    
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-success">
            <CheckCircle size={48} />
            <h3>Request Submitted</h3>
            <p>We'll contact you within 24 hours to confirm your new appointment time.</p>
            <button className="btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Request Reschedule</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="current-appointment">
            <label>Current Appointment</label>
            <p>{format(parseISO(job.date), 'EEEE, MMMM d, yyyy')}</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-field">
              <label>Preferred New Date</label>
              <input
                type="date"
                value={preferredDate}
                onChange={(e) => setPreferredDate(e.target.value)}
                min={format(addDays(new Date(), 2), 'yyyy-MM-dd')}
                required
              />
            </div>

            <div className="form-field">
              <label>Preferred Time</label>
              <select value={preferredTime} onChange={(e) => setPreferredTime(e.target.value)}>
                <option value="flexible">Flexible</option>
                <option value="morning">Morning (8am - 12pm)</option>
                <option value="afternoon">Afternoon (12pm - 5pm)</option>
              </select>
            </div>

            <div className="form-field">
              <label>Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional details..."
                rows={3}
              />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={submitting || !preferredDate}>
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ===== ADD-ON MODAL =====
function AddOnModal({ onClose, nextJob, client, addMessage }) {
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const addons = [
    { id: 'fridge', name: 'Inside Refrigerator', price: 35, time: '30 min' },
    { id: 'oven', name: 'Inside Oven', price: 35, time: '30 min' },
    { id: 'windows-int', name: 'Interior Windows', price: 45, time: '45 min' },
    { id: 'baseboards', name: 'Detailed Baseboards', price: 40, time: '30 min' },
    { id: 'cabinets', name: 'Inside Cabinets', price: 50, time: '45 min' },
    { id: 'garage', name: 'Garage Sweep', price: 30, time: '20 min' },
  ];

  const toggleAddon = (id) => {
    setSelectedAddons(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const selectedTotal = addons
    .filter(a => selectedAddons.includes(a.id))
    .reduce((sum, a) => sum + a.price, 0);

  const handleSubmit = async () => {
    setSubmitting(true);
    
    const selectedServices = addons.filter(a => selectedAddons.includes(a.id));
    const serviceList = selectedServices.map(s => `• ${s.name} - $${s.price}`).join('\n');
    const appointmentDate = nextJob ? format(parseISO(nextJob.date), 'EEEE, MMMM d, yyyy') : 'my next appointment';
    const messageBody = `I would like to add the following services to ${appointmentDate}:\n\n${serviceList}\n\nTotal: $${selectedTotal}`;
    
    try {
      await addMessage({
        clientId: client.id,
        subject: `Add-on request for ${nextJob ? format(parseISO(nextJob.date), 'MMM d') : 'next appointment'}`,
        body: messageBody,
        type: 'addon',
        source: 'addon_modal',
        sender: 'client',
        senderName: `${client.firstName} ${client.lastName}`.trim(),
        senderEmail: client.email,
        relatedJobId: nextJob?.id || null
      });
      
      // Send notification email
      await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'client_message_notification',
          data: {
            clientName: `${client.firstName} ${client.lastName}`.trim(),
            clientEmail: client.email,
            clientPhone: client.phone,
            subject: `Add-on request for ${nextJob ? format(parseISO(nextJob.date), 'MMM d') : 'next appointment'}`,
            message: messageBody
          }
        })
      });
      
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit add-on request:', error);
      alert('Failed to submit request. Please try again.');
    }
    
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-success">
            <CheckCircle size={48} />
            <h3>Add-Ons Requested</h3>
            <p>We've added these services to your next cleaning. Your team will be notified.</p>
            <button className="btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content addon-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add Extra Services</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {nextJob && (
            <p className="addon-note">
              For your {format(parseISO(nextJob.date), 'MMM d')} appointment
            </p>
          )}

          <div className="addon-list">
            {addons.map(addon => (
              <button
                key={addon.id}
                className={`addon-option ${selectedAddons.includes(addon.id) ? 'selected' : ''}`}
                onClick={() => toggleAddon(addon.id)}
              >
                <div className="addon-check">
                  {selectedAddons.includes(addon.id) && <Check size={16} />}
                </div>
                <div className="addon-info">
                  <span className="addon-name">{addon.name}</span>
                  <span className="addon-time">{addon.time}</span>
                </div>
                <span className="addon-price">{formatCurrency(addon.price)}</span>
              </button>
            ))}
          </div>

          {selectedAddons.length > 0 && (
            <div className="addon-total">
              <span>Total Add-Ons</span>
              <span>{formatCurrency(selectedTotal)}</span>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={submitting || selectedAddons.length === 0}
              onClick={handleSubmit}
            >
              {submitting ? 'Submitting...' : `Add ${selectedAddons.length} Service${selectedAddons.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== LAUNDRY MODAL =====
function LaundryModal({ onClose, client, addMessage }) {
  const [pickupDate, setPickupDate] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    const messageBody = `I would like to schedule a laundry pickup.\n\nPickup Date: ${format(parseISO(pickupDate), 'EEEE, MMMM d, yyyy')}\nService: Wash, dry, and fold ($3/lb)${notes ? `\n\nSpecial Instructions: ${notes}` : ''}`;
    
    try {
      await addMessage({
        clientId: client.id,
        subject: 'Laundry pickup request',
        body: messageBody,
        type: 'laundry',
        source: 'laundry_modal',
        sender: 'client',
        senderName: `${client.firstName} ${client.lastName}`.trim(),
        senderEmail: client.email
      });
      
      // Send notification email
      await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'client_message_notification',
          data: {
            clientName: `${client.firstName} ${client.lastName}`.trim(),
            clientEmail: client.email,
            clientPhone: client.phone,
            subject: 'Laundry pickup request',
            message: messageBody
          }
        })
      });
      
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit laundry request:', error);
      alert('Failed to submit request. Please try again.');
    }
    
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-success">
            <CheckCircle size={48} />
            <h3>Pickup Scheduled</h3>
            <p>We'll pick up your laundry on {format(parseISO(pickupDate), 'EEEE, MMM d')} and return it with your next cleaning.</p>
            <button className="btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Schedule Laundry Pickup</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="laundry-info">
            <Shirt size={24} />
            <div>
              <h4>$3 per pound</h4>
              <p>Wash, dry, and fold. Delivered on your next cleaning day.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-field">
              <label>Preferred Pickup Date</label>
              <input
                type="date"
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
                min={format(addDays(new Date(), 2), 'yyyy-MM-dd')}
                required
              />
            </div>

            <div className="form-field">
              <label>Special Instructions (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., Separate colors, delicates..."
                rows={3}
              />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={submitting || !pickupDate}>
                {submitting ? 'Scheduling...' : 'Schedule Pickup'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ===== BOOK EXTRA CLEANING MODAL =====
function BookCleaningModal({ onClose, client, property, addMessage }) {
  const [serviceType, setServiceType] = useState('standard');
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('flexible');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const serviceOptions = [
    { id: 'standard', name: 'Standard Cleaning', description: 'Your regular cleaning service', estimate: 'Based on your home size' },
    { id: 'deep', name: 'Deep Clean', description: 'Extra thorough top-to-bottom cleaning', estimate: 'Starting at $250' },
    { id: 'move', name: 'Move In/Out Clean', description: 'Comprehensive cleaning for moving', estimate: 'Starting at $350' },
    { id: 'post_construction', name: 'Post-Construction', description: 'Heavy duty cleaning after renovations', estimate: 'Quote required' },
  ];

  const timeOptions = [
    { value: 'flexible', label: 'Flexible' },
    { value: 'morning', label: 'Morning (8am - 12pm)' },
    { value: 'afternoon', label: 'Afternoon (12pm - 5pm)' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!preferredDate) {
      alert('Please select a preferred date');
      return;
    }
    
    setSubmitting(true);
    
    const selectedService = serviceOptions.find(s => s.id === serviceType);
    const timeLabel = timeOptions.find(t => t.value === preferredTime)?.label;
    
    const messageBody = `I would like to book an extra cleaning.\n\nService Type: ${selectedService.name}\nPreferred Date: ${format(parseISO(preferredDate), 'EEEE, MMMM d, yyyy')}\nPreferred Time: ${timeLabel}${notes ? `\n\nNotes: ${notes}` : ''}\n\nAddress: On file`;
    
    try {
      await addMessage({
        clientId: client.id,
        subject: `Extra cleaning request - ${selectedService.name}`,
        body: messageBody,
        type: 'booking_request',
        source: 'book_cleaning_modal',
        sender: 'client',
        senderName: `${client.firstName} ${client.lastName}`.trim(),
        senderEmail: client.email,
      });
      
      // Send notification email to admin
      await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'client_message_notification',
          data: {
            clientName: `${client.firstName} ${client.lastName}`.trim(),
            clientEmail: client.email,
            clientPhone: client.phone,
            subject: `Extra cleaning request - ${selectedService.name}`,
            message: messageBody
          }
        })
      });
      
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit booking request:', error);
      alert('Failed to submit request. Please try again.');
    }
    
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-success">
            <CheckCircle size={48} />
            <h3>Request Submitted!</h3>
            <p>We'll contact you within 24 hours to confirm your appointment and provide a quote.</p>
            <button className="btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content book-cleaning-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Book Extra Cleaning</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {/* Service Type */}
            <div className="form-field">
              <label>Service Type</label>
              <div className="service-options">
                {serviceOptions.map(service => (
                  <button
                    key={service.id}
                    type="button"
                    className={`service-option ${serviceType === service.id ? 'selected' : ''}`}
                    onClick={() => setServiceType(service.id)}
                  >
                    <div className="service-check">
                      {serviceType === service.id && <Check size={16} />}
                    </div>
                    <div className="service-info">
                      <strong>{service.name}</strong>
                      <span>{service.description}</span>
                      <span className="estimate">{service.estimate}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Preferred Date */}
            <div className="form-field">
              <label>Preferred Date</label>
              <input
                type="date"
                value={preferredDate}
                onChange={(e) => setPreferredDate(e.target.value)}
                min={format(addDays(new Date(), 2), 'yyyy-MM-dd')}
                required
              />
            </div>

            {/* Preferred Time */}
            <div className="form-field">
              <label>Preferred Time</label>
              <select value={preferredTime} onChange={(e) => setPreferredTime(e.target.value)}>
                {timeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div className="form-field">
              <label>Special Requests or Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any specific areas of focus, access instructions, etc."
                rows={3}
              />
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Request Booking'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ===== ESTIMATE APPROVAL VIEW (Keep existing functionality) =====
function EstimateApprovalView({ estimate, client }) {
  // This is a simplified version - the full implementation is in the original ClientPortal.js
  // For now, redirect to the existing estimate approval flow
  return null;
}

// ===== NEW MESSAGE MODAL =====
function NewMessageModal({ onClose, client, addMessage }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    
    setSubmitting(true);
    
    try {
      await addMessage({
        clientId: client.id,
        subject: subject.trim(),
        body: body.trim(),
        type: 'general',
        source: 'portal',
        sender: 'client',
        senderName: `${client.firstName} ${client.lastName}`.trim(),
        senderEmail: client.email
      });
      
      // Send notification email
      await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'client_message_notification',
          data: {
            clientName: `${client.firstName} ${client.lastName}`.trim(),
            clientEmail: client.email,
            clientPhone: client.phone,
            subject: subject.trim(),
            message: body
          }
        })
      });
      
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    }
    
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-success">
            <CheckCircle size={48} />
            <h3>Message Sent</h3>
            <p>We'll get back to you as soon as possible!</p>
            <button className="btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>New Message</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-field">
              <label>Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="What is this about?"
                required
                autoFocus
              />
            </div>

            <div className="form-field">
              <label>Message</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="How can we help you?"
                rows={5}
                required
              />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn-primary" 
                disabled={submitting || !subject.trim() || !body.trim()}
              >
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ===== PORTAL CONVERSATION VIEW =====
function PortalConversationView({ conversation, client, onBack, onReply, markAsRead }) {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  // Mark as read when viewing
  useEffect(() => {
    if (conversation && markAsRead) {
      markAsRead();
    }
  }, [conversation?.id]);

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    
    setSending(true);
    try {
      await onReply(replyText.trim());
      setReplyText('');
    } catch (error) {
      console.error('Failed to send reply:', error);
      alert('Failed to send reply. Please try again.');
    }
    setSending(false);
  };

  return (
    <div className="conversation-view">
      <div className="conversation-header-portal">
        <button className="btn-back" onClick={onBack}>
          <ArrowLeft size={20} />
          Back
        </button>
        <div className="conversation-title">
          <h3>{conversation.subject}</h3>
          {conversation.status === 'resolved' && (
            <span className="status-badge resolved">
              <CheckCircle size={14} />
              Resolved
            </span>
          )}
        </div>
      </div>

      <div className="conversation-messages">
        {conversation.thread?.map((msg, idx) => (
          <div 
            key={msg.id || idx}
            className={`message-bubble ${msg.sender === 'admin' ? 'from-admin' : 'from-client'}`}
          >
            <div className="message-sender">
              {msg.sender === 'admin' ? 'Two Sisters' : 'You'}
            </div>
            <div className="message-body">{msg.body}</div>
            <div className="message-time">
              {format(parseISO(msg.timestamp), 'MMM d, h:mm a')}
            </div>
          </div>
        ))}
      </div>

      {conversation.status !== 'resolved' && (
        <div className="reply-section">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply..."
            rows={3}
          />
          <button 
            className="btn-send"
            onClick={handleSendReply}
            disabled={!replyText.trim() || sending}
          >
            <Send size={18} />
            {sending ? 'Sending...' : 'Send'}
          </button>
        </div>
      )}
    </div>
  );
}

// ===== COMMUNICATION PREFERENCES COMPONENT =====
function CommunicationPreferences({ client, dispatch }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // Defaults: all notifications ON
  const defaultPrefs = {
    appointment: { email: true, sms: true },
    teamStatus: { email: true, sms: true },
    invoices: { email: true, sms: true },
    estimates: { email: true, sms: true },
    portalMessages: { email: true, sms: true }
  };
  
  // Get current preferences or defaults
  const getPrefs = () => {
    return {
      ...defaultPrefs,
      ...client?.notificationPrefs
    };
  };
  
  const [prefs, setPrefs] = useState(getPrefs);
  const [smsConsent, setSmsConsent] = useState(client?.textConsent !== false);
  
  // Category labels and descriptions
  const categoryConfig = {
    appointment: {
      label: 'Appointment Reminders',
      description: 'Confirmations and reminders before your cleaning'
    },
    teamStatus: {
      label: 'Team Updates',
      description: 'On the way, arrived, running late, and completed'
    },
    invoices: {
      label: 'Invoices & Payments',
      description: 'Invoice sent, payment received, and reminders'
    },
    estimates: {
      label: 'Estimates',
      description: 'When new quotes are ready for your review'
    },
    portalMessages: {
      label: 'Portal Messages',
      description: 'When we reply to your messages'
    }
  };
  
  const togglePreference = (category, channel) => {
    setPrefs(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [channel]: !prev[category]?.[channel]
      }
    }));
    setSaved(false);
  };
  
  const handleSmsConsent = () => {
    const newConsent = !smsConsent;
    setSmsConsent(newConsent);
    
    // If disabling SMS, turn off all SMS preferences
    if (!newConsent) {
      setPrefs(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          updated[key] = { ...updated[key], sms: false };
        });
        return updated;
      });
    }
    setSaved(false);
  };
  
  const handleSave = async () => {
    setSaving(true);
    
    try {
      dispatch({
        type: 'UPDATE_CLIENT',
        payload: {
          id: client.id,
          notificationPrefs: prefs,
          textConsent: smsConsent
        }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save preferences:', error);
      alert('Failed to save preferences. Please try again.');
    }
    
    setSaving(false);
  };
  
  const hasPhone = !!client.phone;
  const hasEmail = !!client.email;
  
  // Check if completely opted out
  const isOptedOut = Object.values(prefs).every(p => !p.email && !p.sms);
  
  return (
    <section className="account-section comm-prefs-section">
      <h3>Communication Preferences</h3>
      
      {isOptedOut && (
        <div className="opted-out-warning">
          <AlertCircle size={16} />
          <span>You're not receiving any notifications. Enable at least one to stay informed.</span>
        </div>
      )}
      
      {/* SMS Consent Toggle */}
      {hasPhone && (
        <div className="sms-consent-box">
          <label className="consent-toggle">
            <input 
              type="checkbox"
              checked={smsConsent}
              onChange={handleSmsConsent}
            />
            <span className="toggle-slider"></span>
            <div className="consent-label">
              <strong>Receive text messages</strong>
              <span>Get updates via SMS to {maskPhone(client.phone)}</span>
            </div>
          </label>
        </div>
      )}
      
      {/* Notification Categories */}
      <div className="prefs-table">
        <div className="prefs-header">
          <span className="pref-category-header">Notification Type</span>
          <span className="pref-channel-header">
            <Mail size={14} />
            Email
          </span>
          {hasPhone && (
            <span className="pref-channel-header">
              <Smartphone size={14} />
              Text
            </span>
          )}
        </div>
        
        {Object.entries(categoryConfig).map(([key, config]) => (
          <div key={key} className="pref-row">
            <div className="pref-info">
              <span className="pref-label">{config.label}</span>
              <span className="pref-description">{config.description}</span>
            </div>
            
            {hasEmail && (
              <label className="pref-toggle">
                <input 
                  type="checkbox"
                  checked={prefs[key]?.email || false}
                  onChange={() => togglePreference(key, 'email')}
                />
                <span className="toggle-check">
                  {prefs[key]?.email && <Check size={12} />}
                </span>
              </label>
            )}
            
            {hasPhone && (
              <label className={`pref-toggle ${!smsConsent ? 'disabled' : ''}`}>
                <input 
                  type="checkbox"
                  checked={prefs[key]?.sms || false}
                  onChange={() => togglePreference(key, 'sms')}
                  disabled={!smsConsent}
                />
                <span className="toggle-check">
                  {prefs[key]?.sms && <Check size={12} />}
                </span>
              </label>
            )}
          </div>
        ))}
      </div>
      
      <button 
        className={`btn-save-prefs ${saved ? 'saved' : ''}`}
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Saving...' : saved ? (
          <>
            <CheckCircle size={16} />
            Saved!
          </>
        ) : 'Save Preferences'}
      </button>
    </section>
  );
}

// ===== OPT-IN PROMPT (shown when completely opted out) =====
function OptInPrompt({ client, dispatch, onDismiss }) {
  const [showPrompt, setShowPrompt] = useState(true);
  
  // Check if completely opted out
  const prefs = client?.notificationPrefs;
  const isOptedOut = prefs && Object.values(prefs).every(p => !p?.email && !p?.sms);
  
  // Only show if opted out AND has been dismissed this session
  const [dismissed, setDismissed] = useState(false);
  
  if (!isOptedOut || !showPrompt || dismissed) return null;
  
  const handleEnableDefaults = () => {
    dispatch({
      type: 'UPDATE_CLIENT',
      payload: {
        id: client.id,
        notificationPrefs: {
          appointment: { email: true, sms: false },
          teamStatus: { email: false, sms: false },
          invoices: { email: true, sms: false },
          estimates: { email: true, sms: false },
          portalMessages: { email: true, sms: false }
        }
      }
    });
    setShowPrompt(false);
    if (onDismiss) onDismiss();
  };
  
  const handleDismiss = () => {
    setDismissed(true);
    setShowPrompt(false);
    if (onDismiss) onDismiss();
  };
  
  return (
    <div className="optin-prompt-overlay">
      <div className="optin-prompt">
        <div className="optin-icon">
          <AlertCircle size={32} />
        </div>
        <h3>Stay Informed</h3>
        <p>
          You're currently opted out of all notifications. Enable notifications to receive 
          appointment reminders, team updates, and invoices.
        </p>
        <div className="optin-actions">
          <button className="btn-optin" onClick={handleEnableDefaults}>
            <Mail size={18} />
            Enable Email Notifications
          </button>
          <button className="btn-dismiss" onClick={handleDismiss}>
            Not Now
          </button>
        </div>
      </div>
    </div>
  );
}

export default ClientPortalMain;
